(() => {
  "use strict";

  const SELECTOR = "[data-account-hub-root]";
  const MAX_VISIBLE_ITEMS = 4;
  const REQUEST_TIMEOUT_MS = 15000;

  function query(root, selector) {
    return root.querySelector(selector);
  }

  function setText(root, selector, value) {
    const node = query(root, selector);
    if (node) node.textContent = String(value ?? "");
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = Boolean(hidden);
  }

  function safePath(value, fallback) {
    const candidate = String(value || "").trim();
    if (
      !candidate.startsWith("/") ||
      candidate.startsWith("//") ||
      candidate.includes("\\") ||
      /[\u0000-\u001f\u007f]/.test(candidate)
    ) return fallback;

    try {
      const parsed = new URL(candidate, window.location.origin);
      if (parsed.origin !== window.location.origin) return fallback;
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return fallback;
    }
  }

  function setSafeLink(root, selector, value, fallback) {
    const link = query(root, selector);
    if (link) link.setAttribute("href", safePath(value, fallback));
  }

  function formatCount(value) {
    const count = Number(value);
    return Number.isFinite(count) && count > 0 ? Math.floor(count).toLocaleString() : "0";
  }

  function formatMoney(cents, currencyCode = "USD") {
    if (cents === null || cents === undefined || !Number.isFinite(Number(cents))) return "Pending";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: String(currencyCode || "USD"),
    }).format(Number(cents) / 100);
  }

  function formatStatus(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function formatDate(value, includeTime = true) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const options = includeTime
      ? { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
      : { month: "short", day: "numeric", year: "numeric" };
    return new Intl.DateTimeFormat(undefined, options).format(date);
  }

  function refreshIcons() {
    if (window.lucide && typeof window.lucide.createIcons === "function") {
      window.lucide.createIcons();
    }
  }

  function createListItem({ title, detail, status, dateTime }) {
    const item = document.createElement("li");
    const copy = document.createElement("div");
    const heading = document.createElement("strong");
    heading.textContent = title || "Darkstone activity";
    copy.appendChild(heading);

    if (detail) {
      const meta = dateTime ? document.createElement("time") : document.createElement("small");
      meta.textContent = detail;
      if (dateTime) meta.setAttribute("datetime", dateTime);
      copy.appendChild(meta);
    }

    item.appendChild(copy);
    if (status) {
      const badge = document.createElement("span");
      badge.className = "dsh-account__item-status";
      badge.textContent = formatStatus(status);
      item.appendChild(badge);
    }
    return item;
  }

  function renderItems(list, empty, items) {
    if (!list) return;
    list.replaceChildren();
    const visible = Array.isArray(items) ? items.slice(0, MAX_VISIBLE_ITEMS) : [];
    visible.forEach((item) => list.appendChild(createListItem(item)));
    setHidden(list, visible.length === 0);
    setHidden(empty, visible.length !== 0);
  }

  function buildPullItems(data) {
    const items = Array.isArray(data?.pullList?.activeItems) ? data.pullList.activeItems : [];
    return items.map((item) => ({
      title: `${item.title || "Pull-list title"}${Number(item.quantity) > 1 ? ` × ${Number(item.quantity)}` : ""}`,
      detail: item.publisher || (item.kind === "SERIES" ? "Series subscription" : "Issue request"),
      status: item.kind === "SERIES" ? "series" : "issue",
    }));
  }

  function buildReservationItems(data) {
    const items = Array.isArray(data?.reservations) ? data.reservations : [];
    return items.map((item) => {
      const holdDate = formatDate(item.holdUntil, false);
      return {
        title: `${item.title || "Reserved item"}${Number(item.quantity) > 1 ? ` × ${Number(item.quantity)}` : ""}`,
        detail: holdDate ? `Held through ${holdDate}` : "Reserved for pickup",
        status: item.status || "open",
        dateTime: item.holdUntil || null,
      };
    });
  }

  function buildTicketItems(data) {
    const items = Array.isArray(data?.tickets) ? data.tickets : [];
    return items.map((item) => {
      const when = formatDate(item.startsAt, true);
      const detail = [when, item.location].filter(Boolean).join(" · ");
      return {
        title: item.eventTitle || "Darkstone event",
        detail: detail || "Upcoming event",
        status: item.status || "active",
        dateTime: item.startsAt || null,
      };
    });
  }

  function buildGameFinderItems(data) {
    const gameFinder = data?.gameFinder || {};
    const requests = Array.isArray(gameFinder.activeRequests) ? gameFinder.activeRequests : [];
    const responses = Array.isArray(gameFinder.recentResponses) ? gameFinder.recentResponses : [];
    const result = [];

    requests.slice(0, 2).forEach((request) => {
      const when = formatDate(request.startsAt, true);
      result.push({
        title: request.title || "Game request",
        detail: [request.gameSystem, when].filter(Boolean).join(" · "),
        status: request.status || "open",
        dateTime: request.startsAt || null,
      });
    });

    responses.slice(0, 2).forEach((response) => {
      const when = formatDate(response.startsAt, true);
      result.push({
        title: response.requestTitle || "Game response",
        detail: [response.gameSystem, when].filter(Boolean).join(" · "),
        status: response.status || "interested",
        dateTime: response.startsAt || null,
      });
    });

    return result;
  }

  function appendWeeklyValueRow(parent, label, value, emphasized = false) {
    const row = document.createElement("div");
    row.className = emphasized ? "dsh-account__weekly-total is-emphasized" : "dsh-account__weekly-total";
    const labelNode = document.createElement(emphasized ? "strong" : "span");
    const valueNode = document.createElement(emphasized ? "strong" : "span");
    labelNode.textContent = label;
    valueNode.textContent = value;
    row.append(labelNode, valueNode);
    parent.appendChild(row);
  }

  function appendWeeklyMoneyRow(parent, label, cents, currencyCode, emphasized = false) {
    appendWeeklyValueRow(parent, label, formatMoney(cents, currencyCode), emphasized);
  }

  function createWeeklyAction(label, intent, batchId, expectedVersion, method, primary = false) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `dsh-account__button${primary ? " dsh-account__button--primary" : ""}`;
    button.textContent = method && primary ? `${label} — selected` : label;
    button.dataset.accountHubPullIntent = intent;
    button.dataset.accountHubPullBatchId = String(batchId || "");
    button.dataset.accountHubPullExpectedVersion = String(Math.max(0, Number(expectedVersion) || 0));
    if (method) {
      button.dataset.accountHubPullMethod = method;
      button.setAttribute("aria-pressed", primary ? "true" : "false");
      button.classList.toggle("is-selected", primary);
    }
    return button;
  }

  function findWeeklyBatchCard(root, batchId) {
    return Array.from(root.querySelectorAll(".dsh-account__weekly-batch"))
      .find((card) => card.dataset.accountHubPullBatchId === batchId) || null;
  }

  function renderWeeklyBatchFeedback(card, feedback = {}) {
    if (!card) return;
    const status = query(card, "[data-account-hub-pull-status]");
    const error = query(card, "[data-account-hub-pull-error]");
    if (status) status.textContent = String(feedback.status || "");
    if (error) error.textContent = String(feedback.error || "");
    setHidden(status, !feedback.status);
    setHidden(error, !feedback.error);
  }

  function updateWeeklyBatchFeedback(root, feedbackById, batchId, feedback) {
    feedbackById.set(batchId, { status: String(feedback.status || ""), error: String(feedback.error || "") });
    renderWeeklyBatchFeedback(findWeeklyBatchCard(root, batchId), feedbackById.get(batchId));
  }

  function syncWeeklyBatchBusyState(root, batchId, isBusy) {
    const card = findWeeklyBatchCard(root, batchId);
    if (!card) return;
    if (isBusy) card.setAttribute("aria-busy", "true");
    else card.removeAttribute("aria-busy");
    card.querySelectorAll("[data-account-hub-pull-intent]").forEach((actionButton) => {
      actionButton.disabled = isBusy;
    });
  }

  function focusWeeklyBatchFallback(root, batchId) {
    const card = findWeeklyBatchCard(root, batchId);
    const target = card?.querySelector("h3") || card;
    if (!target) return;
    target.tabIndex = -1;
    target.focus();
  }

  function renderWeeklyPulls(root, data, activeWeeklyBatchIds, weeklyBatchFeedbackById) {
    const container = query(root, "[data-account-hub-weekly-pulls]");
    const empty = query(root, "[data-account-hub-weekly-empty]");
    if (!container) return;
    container.replaceChildren();
    const batches = Array.isArray(data.weeklyPulls) ? data.weeklyPulls.slice(0, 12) : [];
    batches.forEach((batch) => {
      const batchId = String(batch.id || "");
      const isBusy = activeWeeklyBatchIds.has(batchId);
      const card = document.createElement("article");
      card.className = "dsh-account__weekly-batch";
      card.dataset.accountHubPullBatchId = batchId;
      if (isBusy) card.setAttribute("aria-busy", "true");

      const head = document.createElement("div");
      head.className = "dsh-account__weekly-head";
      const headingWrap = document.createElement("div");
      const heading = document.createElement("h3");
      heading.textContent = batch.weekLabel || "Weekly pulls";
      const status = document.createElement("span");
      status.className = "dsh-account__tag";
      status.textContent = batch.statusLabel || formatStatus(batch.status);
      headingWrap.appendChild(heading);
      head.append(headingWrap, status);
      card.appendChild(head);

      const feedbackStatus = document.createElement("p");
      feedbackStatus.className = "dsh-account__weekly-status";
      feedbackStatus.dataset.accountHubPullStatus = "";
      feedbackStatus.setAttribute("role", "status");
      feedbackStatus.setAttribute("aria-live", "polite");
      const feedbackError = document.createElement("p");
      feedbackError.className = "dsh-account__weekly-error";
      feedbackError.dataset.accountHubPullError = "";
      feedbackError.setAttribute("role", "alert");
      feedbackError.setAttribute("aria-live", "assertive");
      card.append(feedbackStatus, feedbackError);
      renderWeeklyBatchFeedback(card, weeklyBatchFeedbackById.get(batchId));

      const itemList = document.createElement("ul");
      itemList.className = "dsh-account__weekly-items";
      const currencyCode = batch?.money?.currencyCode || "USD";
      const items = Array.isArray(batch.items) ? batch.items : [];
      items.forEach((item) => {
        const line = document.createElement("li");
        const itemCopy = document.createElement("span");
        const title = document.createElement("span");
        const unitPrice = document.createElement("small");
        const amount = document.createElement("strong");
        const quantity = Math.max(1, Number(item.quantity) || 1);
        title.textContent = `${item.title || "Pulled item"} × ${quantity}`;
        unitPrice.textContent = item.unitPriceCents == null
          ? "Unit price pending"
          : `${formatMoney(item.unitPriceCents, currencyCode)} each`;
        amount.textContent = `Line total: ${formatMoney(item.lineTotalCents, currencyCode)}`;
        itemCopy.append(title, unitPrice);
        line.append(itemCopy, amount);
        itemList.appendChild(line);
      });
      card.appendChild(itemList);

      const displayedItemCount = Math.max(0, Number(batch.displayedItemCount) || items.length);
      const totalItemCount = Math.max(displayedItemCount, Number(batch.totalItemCount) || displayedItemCount);
      if (batch.itemsTruncated || totalItemCount > displayedItemCount) {
        const itemNotice = document.createElement("p");
        itemNotice.className = "dsh-account__weekly-notice";
        itemNotice.textContent = `Showing ${formatCount(displayedItemCount)} of ${formatCount(totalItemCount)} items. Payment is unavailable until every charged item can be reviewed.`;
        card.appendChild(itemNotice);
      }

      const totals = document.createElement("div");
      totals.className = "dsh-account__weekly-totals";
      appendWeeklyMoneyRow(totals, "Subtotal", batch.money.subtotalCents, currencyCode);
      if (Number(batch.money.discountCents) > 0) {
        appendWeeklyMoneyRow(totals, "Pull-list savings", -Math.abs(Number(batch.money.discountCents)), currencyCode);
      }
      if (Number(batch.money.shippingCents) > 0) {
        appendWeeklyMoneyRow(totals, "Shipping", batch.money.shippingCents, currencyCode);
      }
      if (batch.money.taxCents == null) {
        appendWeeklyValueRow(totals, "Tax", "Pending");
      } else {
        appendWeeklyMoneyRow(totals, "Tax", batch.money.taxCents, currencyCode);
      }
      appendWeeklyMoneyRow(
        totals,
        batch.money.calculationState === "COMPLETE" ? "Total" : "Estimated total",
        batch.money.totalCents,
        currencyCode,
        true,
      );
      card.appendChild(totals);

      if (batch.fulfillmentMethod) {
        const selected = document.createElement("p");
        selected.className = "dsh-account__weekly-selected";
        selected.textContent = batch.fulfillmentMethod === "SHIPPING"
          ? `Selected: Shipping — ${formatMoney(batch.money.shippingCents, currencyCode)}`
          : "Selected: Free in-store pickup";
        card.appendChild(selected);
      }

      const actions = document.createElement("div");
      actions.className = "dsh-account__weekly-actions";
      if (!batch.fulfillmentLocked && !["PAID", "COMPLETED", "CANCELED"].includes(String(batch.status || ""))) {
        actions.appendChild(createWeeklyAction(
          "Free in-store pickup",
          "select-fulfillment",
          batch.id,
          batch.lifecycleVersion,
          "PICKUP",
          batch.fulfillmentMethod === "PICKUP",
        ));
        const shipping = batch.fulfillmentOptions.shipping;
        if (shipping.available && Number.isInteger(shipping.priceCents) && shipping.priceCents > 0) {
          actions.appendChild(createWeeklyAction(
            `Ship this batch — ${formatMoney(shipping.priceCents, currencyCode)}`,
            "select-fulfillment",
            batch.id,
            batch.lifecycleVersion,
            "SHIPPING",
            batch.fulfillmentMethod === "SHIPPING",
          ));
        } else {
          const unavailable = document.createElement("p");
          unavailable.className = "dsh-account__weekly-notice";
          unavailable.textContent = "Shipping price unavailable. Reload your account or choose free pickup.";
          actions.appendChild(unavailable);
        }
      }
      if (batch.payable && batch.money.calculationState === "COMPLETE" && !batch.itemsTruncated && totalItemCount === displayedItemCount) {
        actions.appendChild(createWeeklyAction("Pay securely with Shopify", "checkout", batch.id, batch.lifecycleVersion, null, true));
      }
      if (actions.childElementCount > 0) card.appendChild(actions);
      actions.querySelectorAll("[data-account-hub-pull-intent]").forEach((actionButton) => {
        actionButton.disabled = isBusy;
      });
      container.appendChild(card);
    });
    const totalBatchCount = Math.max(batches.length, Number(data.weeklyPullTotalCount) || batches.length);
    if (totalBatchCount > batches.length) {
      const historyNotice = document.createElement("p");
      historyNotice.className = "dsh-account__weekly-notice";
      historyNotice.textContent = `Showing ${formatCount(batches.length)} of ${formatCount(totalBatchCount)} weekly batches. Older pull-payment history is available from Darkstone staff.`;
      container.appendChild(historyNotice);
    }
    setHidden(container, batches.length === 0);
    setHidden(empty, batches.length !== 0);
  }

  function validateHubPayload(data) {
    const isObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);
    const isCount = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0;
    const weeklyContract = globalThis.DarkstoneWeeklyPullContract;
    const version = Number(data?.version);
    const valid =
      isObject(data) &&
      (version === 2 || version === 3) &&
      isObject(data.customer) &&
      isObject(data.guild) &&
      isCount(data.guild.availablePoints) &&
      isCount(data.guild.reservedPoints) &&
      isObject(data.totals) &&
      isCount(data.totals.pullListItems) &&
      isCount(data.totals.reservations) &&
      isCount(data.totals.upcomingTickets) &&
      isObject(data.pullList) &&
      isCount(data.pullList.activeCount) &&
      Array.isArray(data.pullList.activeItems) &&
      Array.isArray(data.reservations) &&
      Array.isArray(data.tickets) &&
      isObject(data.gameFinder) &&
      isCount(data.gameFinder.activeRequestCount) &&
      isCount(data.gameFinder.confirmedMatchCount) &&
      Array.isArray(data.gameFinder.activeRequests) &&
      Array.isArray(data.gameFinder.recentResponses) &&
      isObject(data.links) &&
      typeof weeklyContract?.normalizePayload === "function";

    if (!valid) {
      throw new Error("Your account returned incomplete data. Please try again.");
    }
    return weeklyContract.normalizePayload(data);
  }

  function renderDashboard(root, data, activeWeeklyBatchIds, weeklyBatchFeedbackById) {
    const firstName = String(data?.customer?.firstName || "").trim();
    const displayName = String(data?.customer?.displayName || "Guild Member").trim();
    setText(root, "[data-account-hub-greeting]", `Welcome back, ${firstName || displayName}.`);

    const generated = formatDate(data?.generatedAt, true);
    setText(root, "[data-account-hub-updated]", generated ? `Updated ${generated}` : "Your account tools are ready.");

    const points = Number(data?.guild?.availablePoints) || 0;
    const reservedPoints = Number(data?.guild?.reservedPoints) || 0;
    const pullCount = Number(data.totals.pullListItems);
    const reservationCount = Number(data.totals.reservations);
    const ticketCount = Number(data.totals.upcomingTickets);

    setText(root, "[data-account-hub-points]", formatCount(points));
    setText(root, "[data-account-hub-points-large]", formatCount(points));
    setText(root, "[data-account-hub-pull-count]", formatCount(pullCount));
    setText(root, "[data-account-hub-pull-count-inline]", formatCount(pullCount));
    setText(root, "[data-account-hub-reservation-count]", formatCount(reservationCount));
    setText(root, "[data-account-hub-reservation-count-inline]", formatCount(reservationCount));
    setText(root, "[data-account-hub-ticket-count]", formatCount(ticketCount));
    setText(root, "[data-account-hub-ticket-count-inline]", formatCount(ticketCount));

    setText(root, "[data-account-hub-guild-status]", data?.guild?.enrolled ? "Guild member" : "Not enrolled");
    setText(
      root,
      "[data-account-hub-reserved-points]",
      reservedPoints > 0
        ? `${formatCount(reservedPoints)} Coins reserved for active checkout activity.`
        : "No Coins currently reserved.",
    );

    const memberCodeWrap = query(root, "[data-account-hub-member-code-wrap]");
    const memberCode = String(data?.guild?.barcode || "").trim();
    setText(root, "[data-account-hub-member-code]", memberCode);
    setHidden(memberCodeWrap, !memberCode);
    renderWeeklyPulls(root, data, activeWeeklyBatchIds, weeklyBatchFeedbackById);

    renderItems(
      query(root, "[data-account-hub-pull-list]"),
      query(root, "[data-account-hub-pull-empty]"),
      buildPullItems(data),
    );
    renderItems(
      query(root, "[data-account-hub-reservations]"),
      query(root, "[data-account-hub-reservations-empty]"),
      buildReservationItems(data),
    );
    renderItems(
      query(root, "[data-account-hub-tickets]"),
      query(root, "[data-account-hub-tickets-empty]"),
      buildTicketItems(data),
    );

    const gameFinder = data?.gameFinder || {};
    const requestCount = Number(gameFinder.activeRequestCount) || 0;
    const confirmedCount = Number(gameFinder.confirmedMatchCount) || 0;
    setText(root, "[data-account-hub-game-requests]", formatCount(requestCount));
    setText(root, "[data-account-hub-game-confirmed]", formatCount(confirmedCount));
    setText(
      root,
      "[data-account-hub-game-finder-status]",
      gameFinder.paused ? "Paused" : gameFinder.enrolled ? "Profile active" : "Get started",
    );
    setText(
      root,
      "[data-account-hub-game-copy]",
      gameFinder.paused
        ? "Your profile is paused. Open Game Finder whenever you're ready to return."
        : gameFinder.enrolled
          ? "Your player profile connects requests, responses, and confirmed games."
          : "Create a player profile to find opponents and schedule games at Darkstone.",
    );
    renderItems(query(root, "[data-account-hub-game-activity]"), null, buildGameFinderItems(data));

    const links = data?.links || {};
    setSafeLink(root, "[data-account-hub-guild-link]", links.guildLedger, "/apps/portal/guild-ledger");
    setSafeLink(root, "[data-account-hub-pull-link]", links.pullList, "/pages/pull-list");
    setSafeLink(root, "[data-account-hub-events-link]", links.events, "/pages/events");
    setSafeLink(root, "[data-account-hub-game-link]", links.gameFinder, "/apps/portal/game-finder");
    setSafeLink(root, "[data-account-hub-tcg-link]", links.tcgPortal, "/pages/tcg-singles");
    setSafeLink(root, "[data-account-hub-commission-link]", links.commissionBoard, "/apps/portal/commission-board");
    setSafeLink(root, "[data-account-hub-paint-link]", links.paintGuild, "/apps/portal/paint-guild");
  }

  function initialize(root) {
    if (!root || root.dataset.accountHubInitialized === "true") return;
    root.dataset.accountHubInitialized = "true";
    if (root.dataset.accountHubPageTitle) document.title = root.dataset.accountHubPageTitle;
    document.querySelectorAll("[data-my-darkstone-nav]").forEach((link) => {
      link.classList.add("is-active");
      link.setAttribute("aria-current", "page");
    });
    refreshIcons();

    if (root.dataset.accountHubAuthenticated !== "true") return;

    const loading = query(root, "[data-account-hub-loading]");
    const errorPanel = query(root, "[data-account-hub-error]");
    const errorMessage = query(root, "[data-account-hub-error-message]");
    const content = query(root, "[data-account-hub-content]");
    const retry = query(root, "[data-account-hub-retry]");
    const activeWeeklyBatchIds = new Set();
    const weeklyBatchFeedbackById = new Map();
    let controller = null;

    async function load() {
      if (controller) controller.abort();
      const requestController = new AbortController();
      const manageFocus = Boolean(errorPanel?.contains(document.activeElement));
      controller = requestController;
      const timeout = window.setTimeout(() => requestController.abort(), REQUEST_TIMEOUT_MS);
      root.setAttribute("aria-busy", "true");
      setHidden(loading, false);
      if (manageFocus && loading) loading.focus();
      setHidden(errorPanel, true);
      setHidden(content, true);

      try {
        const response = await fetch(root.dataset.accountHubEndpoint || "/apps/portal/account-hub", {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          signal: requestController.signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Unable to load your account (${response.status}).`);
        }
        const data = validateHubPayload(payload);
        if (controller !== requestController) return;
        renderDashboard(root, data, activeWeeklyBatchIds, weeklyBatchFeedbackById);
        setHidden(loading, true);
        setHidden(errorPanel, true);
        setHidden(content, false);
        root.setAttribute("aria-busy", "false");
        if (manageFocus && content) content.focus();
        refreshIcons();
      } catch (error) {
        if (controller !== requestController) return;
        if (error?.name === "AbortError") {
          if (errorMessage) errorMessage.textContent = "The account request took too long. Please try again.";
        } else if (errorMessage) {
          errorMessage.textContent = error?.message || "Your account is temporarily unavailable.";
        }
        setHidden(loading, true);
        setHidden(content, true);
        setHidden(errorPanel, false);
        root.setAttribute("aria-busy", "false");
        if (manageFocus && errorPanel) errorPanel.focus();
        refreshIcons();
      } finally {
        window.clearTimeout(timeout);
        if (controller === requestController) controller = null;
      }
    }

    async function handleWeeklyPullAction(event) {
      const button = event.target?.closest?.("[data-account-hub-pull-intent]");
      if (!button || !root.contains(button)) return;
      const intent = String(button.dataset.accountHubPullIntent || "");
      const batchId = String(button.dataset.accountHubPullBatchId || "").trim();
      const method = String(button.dataset.accountHubPullMethod || "").trim();
      const expectedVersion = String(button.dataset.accountHubPullExpectedVersion || "").trim();
      if (!batchId || !/^\d+$/.test(expectedVersion) || !["select-fulfillment", "checkout"].includes(intent)) return;
      const card = button.closest(".dsh-account__weekly-batch");
      if (!card || activeWeeklyBatchIds.has(batchId)) return;
      event.preventDefault();
      activeWeeklyBatchIds.add(batchId);
      const base = safePath(root.dataset.accountHubPullActionBase, "/apps/portal/pull-batches");
      const action = safePath(`${base}/${encodeURIComponent(batchId)}`, "/apps/portal/my-darkstone");
      syncWeeklyBatchBusyState(root, batchId, true);
      updateWeeklyBatchFeedback(root, weeklyBatchFeedbackById, batchId, {
        status: "Updating weekly pulls…",
        error: "",
      });
      try {
        const formData = new FormData();
        formData.set("intent", intent);
        formData.set("expectedVersion", expectedVersion);
        if (method) formData.set("method", method);
        const response = await fetch(action, {
          method: "POST",
          body: formData,
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          const message = payload?.error || (response.status === 409
            ? "This weekly batch changed since you reviewed it. The latest details are now loaded; please review them and try again."
            : `Unable to update weekly pulls (${response.status}).`);
          if (response.status === 409) await load();
          throw new Error(message);
        }
        if (intent === "checkout") {
          const checkoutUrl = new URL(String(payload?.checkoutUrl || ""));
          const host = checkoutUrl.hostname.toLowerCase();
          const allowed = new Set([
            "darkstonecomics.myshopify.com",
            "darkstonecomics.com",
            "www.darkstonecomics.com",
          ]).has(host);
          if (checkoutUrl.protocol !== "https:" || !allowed || checkoutUrl.username || checkoutUrl.password) {
            throw new Error("Shopify did not return a secure checkout URL.");
          }
          updateWeeklyBatchFeedback(root, weeklyBatchFeedbackById, batchId, {
            status: "Opening secure Shopify checkout…",
            error: "",
          });
          window.location.assign(checkoutUrl.toString());
          return;
        }
        await load();
        updateWeeklyBatchFeedback(root, weeklyBatchFeedbackById, batchId, {
          status: `${method === "SHIPPING" ? "Shipping" : "In-store pickup"} selection saved.`,
          error: "",
        });
        const replacement = Array.from(root.querySelectorAll("[data-account-hub-pull-intent]"))
          .find((candidate) => candidate.dataset.accountHubPullBatchId === batchId
            && candidate.dataset.accountHubPullIntent === intent
            && String(candidate.dataset.accountHubPullMethod || "") === method);
        if (replacement) replacement.focus();
        else focusWeeklyBatchFallback(root, batchId);
      } catch (error) {
        updateWeeklyBatchFeedback(root, weeklyBatchFeedbackById, batchId, {
          status: "",
          error: error?.message || "Unable to update weekly pulls.",
        });
        const currentCard = findWeeklyBatchCard(root, batchId);
        const currentError = currentCard ? query(currentCard, "[data-account-hub-pull-error]") : null;
        if (currentError) {
          currentError.tabIndex = -1;
          currentError.focus();
        } else {
          focusWeeklyBatchFallback(root, batchId);
        }
      } finally {
        activeWeeklyBatchIds.delete(batchId);
        syncWeeklyBatchBusyState(root, batchId, false);
      }
    }

    root.addEventListener("click", handleWeeklyPullAction);
    if (retry) retry.addEventListener("click", load);
    window.addEventListener("pagehide", () => controller?.abort(), { once: true });
    load();
  }

  function start() {
    document.querySelectorAll(SELECTOR).forEach(initialize);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target?.matches?.(SELECTOR) ? event.target : event.target?.querySelector?.(SELECTOR);
    if (root) initialize(root);
  });
})();
